/**
 * Campaign helper utilities.
 * Prize grouping/formatting is driven by backend total_prizes; see use-prizepool.
 */

import { languageFieldSuffixes } from "./language";

/** Player-facing label per wire campaign `type` code. Deliberately the same
 *  i18n keys the type badges render, so a card's badge and its fallback title
 *  can never drift apart. */
const CAMPAIGN_TYPE_LABELS: Record<string, [key: string, fallback: string]> = {
  DP: ["campaign.typePrizeDrop", "Prize drop"],
  TO: ["campaign.typeTournament", "Tournament"],
  RA: ["campaign.typeRace", "Race"],
  JA: ["campaign.typeJackpot", "Jackpot"],
  RF: ["campaign.typeRaffle", "Raffle"],
};

/**
 * What to show as a campaign's title anywhere in the widget.
 *
 * `name` carries the operator's translated overlay title, and is empty when
 * none is set — the backend deliberately does NOT fall back to the internal
 * (backoffice) campaign name there, because players must never see it
 * ("Dynamic Prize Drop Internal"). So an untitled campaign falls back to its
 * type — "Prize drop" reads as something, an internal name reads as a leak.
 */
export const campaignDisplayTitle = (
  campaign: { name?: string; type?: string },
  t: (key: string, options: { defaultValue: string }) => string
): string => {
  const title = campaign.name?.trim();
  if (title) return title;

  const [key, fallback] = CAMPAIGN_TYPE_LABELS[campaign.type ?? ""] ?? [
    "campaign.defaultName",
    "Campaign",
  ];
  return t(key, { defaultValue: fallback });
};

/** The type badge label for a campaign, or null when the wire type has none. Missions are
 *  listed here but NOT in `CAMPAIGN_TYPE_LABELS`: that map doubles as the untitled-campaign
 *  title fallback, and a bare "Missions" title is not what an operator wants players to read. */
export const campaignTypeLabel = (
  campaign: { type?: string },
  t: (key: string, options: { defaultValue: string }) => string
): string | null => {
  const entry =
    CAMPAIGN_TYPE_LABELS[campaign.type ?? ""] ??
    (campaign.type === "MI"
      ? (["missions.typeLabel", "Missions"] as [string, string])
      : undefined);
  if (!entry) return null;
  const [key, fallback] = entry;
  return t(key, { defaultValue: fallback });
};

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th", 11 → "11th", 21 → "21st", etc. */
export const formatOrdinal = (n: number): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  if (mod10 === 1) return `${n}st`;
  if (mod10 === 2) return `${n}nd`;
  if (mod10 === 3) return `${n}rd`;
  return `${n}th`;
};

/** Formats place range with ordinals: "1st", "1st – 3rd", "4th+", etc. Single position shows only the ordinal. */
export const formatPlaceRange = (
  placeFrom: number | null,
  placeTo: number | null
): string => {
  if (placeFrom !== null && placeTo !== null) {
    if (placeFrom === placeTo) return formatOrdinal(placeFrom);
    return `${formatOrdinal(placeFrom)} – ${formatOrdinal(placeTo)}`;
  }
  if (placeFrom !== null) return `${formatOrdinal(placeFrom)}+`;
  if (placeTo !== null) return `– ${formatOrdinal(placeTo)}`;
  return "";
};

/**
 * Normalizes campaign body text for display: HTML line breaks and literal \n to newlines.
 */
export const formatCampaignBody = (body: string) => {
  if (!body) return "";
  return body
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\\n/g, "\n")
    .trim();
};

export const HAS_BLOCK_HTML = /<(?:p|ul|ol|li|div|h[1-6])[\s>/]/i;

/** Converts plain-text body (with literal \n / <br>) into HTML with <br/> separators. */
export const toHtmlFromPlainText = (body: string): string =>
  formatCampaignBody(body)
    .replace(/\n{2,}/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");

/**
 * Body copy (descriptions, T&C) as renderable HTML. Operators author it either as
 * markup or as plain text whose paragraph delimiter is a literal `\n\n` — the
 * latter must become real breaks, or the player sees the escape sequence itself.
 * Markup bodies keep their tags but still get their literal `\n` collapsed to
 * whitespace, since there the tags already do the spacing.
 */
export const campaignBodyHtml = (body: string): string =>
  HAS_BLOCK_HTML.test(body)
    ? body.replace(/\\n/g, "\n")
    : toHtmlFromPlainText(body);

/**
 * Picks terms content by language, walking the same fallback chain the backend uses to name these
 * fields: `content_pt_br` -> `content_pt` -> `content_en` -> `content`. The suffixes are
 * underscore-separated because they come from Django modeltranslation, so a dashed wire code
 * (`pt-br`) must be converted rather than interpolated as-is.
 * @param terms - Campaign terms object (content, content_en, content_de, etc.)
 * @param lang - Optional language code, e.g. "de" or "pt-br"
 */
export const getTermsContent = (
  terms: Record<string, unknown> | null | undefined,
  lang: string | null | undefined
): string => {
  if (!terms) return "";

  const candidates = lang
    ? languageFieldSuffixes(lang).map((suffix) => `content_${suffix}`)
    : [];

  for (const key of [...candidates, "content"]) {
    const value = terms[key];
    if (typeof value === "string" && value.trim()) return value;
  }

  return "";
};
